import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Filter as FilterIcon, Search, X } from 'lucide-react';
import { activityToolbarButtonClass, popupRowItemClass } from 'features/activity/styles/dataTableStyles';

/**
 * One Filter button. The popup lists the filter categories with a muted
 * summary of each; clicking one opens its options (search when the list is
 * long, checkbox multi-select). Changes apply as they are made. Sections are
 * { id, label, options: [{ value, label, dot? }], selected, onChange,
 * emptyIsAny?, onQuery?, footer?, loading? }. With onQuery the search box
 * is the caller's (server-side) and options are not filtered here.
 */
const SEARCH_FROM = 8;

const names = (s) => s.options.filter((o) => s.selected.includes(o.value)).map((o) => o.label);
export const isAllSelected = (s) => (s.emptyIsAny
  ? s.selected.length === 0
  : s.options.every((o) => s.selected.includes(o.value)));
export const summarize = (s) => {
  if (isAllSelected(s)) return 'All';
  const n = names(s);
  if (n.length === 0) return s.selected.length ? `${s.selected.length} selected` : 'None';
  return n.length <= 2 ? n.join(', ') : `${n.length} selected`;
};

export function FilterChip({ label, children, onRemove }) {
  return (
    <span className="inline-flex h-8 items-center gap-1 rounded-lg border border-[color:var(--border-default)] bg-surface pl-2.5 pr-1 text-[12px] text-[color:var(--text-primary)]">
      <span className="text-[color:var(--text-muted)]">{label}</span>
      <span className="max-w-[200px] truncate">{children}</span>
      <button type="button" onClick={onRemove} aria-label={`Remove ${label} filter`} title={`Remove ${label} filter`}
        className="flex h-6 w-6 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]">
        <X size={13} aria-hidden />
      </button>
    </span>
  );
}

export function FilterPanel({ sections, label = 'Filter', onSection, onClose, className = '' }) {
  const [openId, setOpenId] = useState(null);
  const [q, setQ] = useState('');
  const root = useRef(null);
  const s = sections.find((x) => x.id === openId) || null;

  const go = (id) => { setOpenId(id); setQ(''); if (onSection) onSection(id); if (sections.find((x) => x.id === id)?.onQuery) sections.find((x) => x.id === id).onQuery(''); };
  const search = (v) => { setQ(v); if (s?.onQuery) s.onQuery(v); };

  const needle = q.trim().toLowerCase();
  const shown = useMemo(() => (!s ? [] : needle && !s.onQuery ? s.options.filter((o) => o.label.toLowerCase().includes(needle)) : s.options), [s, needle]);
  const searchable = s && (s.onQuery || s.options.length >= SEARCH_FROM);

  useEffect(() => {
    if (!root.current) return;
    const first = root.current.querySelector(searchable ? 'input[type="text"]' : '[data-row]');
    if (first) first.focus();
  }, [openId, searchable]);

  const toggle = (v) => s.onChange(s.selected.includes(v) ? s.selected.filter((x) => x !== v) : [...s.selected, v]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (s) go(null); else onClose();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
    const rows = Array.from(root.current.querySelectorAll('[data-row]'));
    const at = rows.indexOf(document.activeElement);
    if (e.key === 'Enter') {
      if (at >= 0 && document.activeElement.type === 'checkbox') { e.preventDefault(); document.activeElement.click(); }
      return;
    }
    e.preventDefault();
    const next = e.key === 'ArrowDown' ? Math.min(rows.length - 1, at + 1) : at - 1;
    if (next < 0) { const inp = root.current.querySelector('input[type="text"]'); if (inp) inp.focus(); return; }
    if (rows[next]) rows[next].focus();
  };

  return (
    <div ref={root} role="dialog" aria-label={label} onKeyDown={onKeyDown}
      className={`flex max-h-[420px] w-[280px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised,var(--surface-base))] shadow-token-lg ${className}`}>
      {!s ? (
        <div className="p-1">
          {sections.map((x) => (
            <button key={x.id} type="button" data-row onClick={() => go(x.id)}
              className="flex h-8 w-full items-center gap-2 rounded-md px-3 text-left text-[12px] text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover focus:bg-surface-hover focus:outline-none">
              <span className="shrink-0">{x.label}</span>
              <span className="ml-auto min-w-0 truncate text-[11px] text-[color:var(--text-muted)]">{summarize(x)}</span>
              <ChevronRight size={13} className="shrink-0 text-[color:var(--text-muted)]" aria-hidden />
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="flex h-9 shrink-0 items-center gap-1 border-b border-[color:var(--border-default)] pl-1 pr-3">
            <button type="button" data-row onClick={() => go(null)} aria-label="Back to filters" title="Back"
              className="flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] focus:outline-none focus-visible:bg-surface-hover">
              <ChevronLeft size={14} aria-hidden />
            </button>
            <p className="text-[12px] font-medium text-[color:var(--text-primary)]">{s.label}</p>
            {!isAllSelected(s) && (
              <button type="button" onClick={() => s.onChange(s.emptyIsAny ? [] : s.options.map((o) => o.value))}
                className="ml-auto text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">
                {s.emptyIsAny ? 'Clear' : 'All'}
              </button>
            )}
          </div>
          {searchable && (
            <div className="flex h-9 shrink-0 items-center gap-2 border-b border-[color:var(--border-default)] px-3">
              <Search size={13} className="shrink-0 text-[color:var(--text-muted)]" aria-hidden />
              <input type="text" value={q} onChange={(e) => search(e.target.value)} placeholder={`Search ${s.label.toLowerCase()}…`} aria-label={`Search ${s.label.toLowerCase()}`}
                className="w-full border-0 bg-transparent text-[12px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-disabled)] focus:ring-0" />
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto p-1">
            {shown.length === 0 && <p className="px-3 py-3 text-[12px] text-[color:var(--text-muted)]">{s.loading ? 'Loading…' : 'No matches.'}</p>}
            {shown.map((o) => (
              <label key={o.value} className={popupRowItemClass}>
                <input type="checkbox" data-row checked={s.selected.includes(o.value)} onChange={() => toggle(o.value)}
                  className="h-3.5 w-3.5 shrink-0 rounded border-line-strong accent-[color:var(--trodo-primary)] focus:ring-0 focus:ring-offset-0" />
                {o.dot ? <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: o.dot }} /> : null}
                <span className="truncate">{o.label}</span>
              </label>
            ))}
          </div>
          {s.footer ? <div className="shrink-0 border-t border-[color:var(--border-default)] px-3 py-1.5 text-[11px] text-[color:var(--text-muted)]">{s.footer}</div> : null}
        </>
      )}
    </div>
  );
}

export function useDismiss(open, setOpen, ref) {
  useEffect(() => {
    if (!open) return undefined;
    const down = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', down);
    return () => document.removeEventListener('mousedown', down);
  }, [open, setOpen, ref]);
}

export default function IssuesFilter({ sections, dialogLabel = 'Filter' }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useDismiss(open, setOpen, box);
  const active = sections.filter((s) => !isAllSelected(s));
  const reset = (s) => s.onChange(s.emptyIsAny ? [] : s.options.map((o) => o.value));

  return (
    <div ref={box} className="relative flex flex-wrap items-center gap-2">
      {active.map((s) => <FilterChip key={s.id} label={s.label} onRemove={() => reset(s)}>{summarize(s)}</FilterChip>)}
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="dialog" title="Filter" className={activityToolbarButtonClass}>
        <FilterIcon size={13} aria-hidden />
        Filter
        {active.length > 0 && <span className="text-[11px] tabular-nums text-[color:var(--text-muted)]" data-testid="filter-count">{active.length}</span>}
      </button>
      {open && <FilterPanel sections={sections} label={dialogLabel} onClose={() => setOpen(false)} className="absolute right-0 top-full z-50 mt-1" />}
    </div>
  );
}
